import React, { useState } from "react";
import { Box, Typography, Snackbar, Alert, Button } from "@mui/material";
import CustomInput from "@/components/CustomInput/CustomInput";
import MultiSelectDropdown from "@/components/CustomInput/MultiDropDown";
import {
  useScriptGeneration,
  useTeams,
  useUpdateScriptMeta,
  useUsers,
  useGeneratePersonalizedScriptSections,
  useGetRepInsights,
  useTargetCompanies,
  useCompanyContacts,
  useGetCompanyInsights
} from "@/hooks/useTenantDashboardService";
import useAuthStore from "@/store/useAuthStore";
import ScriptListView from "./ScriptListView";
import RepInsightsList from "./RepInsightsList";
import CompanyInsightsList from "./CompanyInsightsList";
import type { ScriptDetails } from "@/types/script";
import type { SectionDto } from "@/types/tenantService";
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";

type User = {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  [key: string]: string | boolean | undefined;
};

const Script: React.FC = () => {
  const { isDark } = useDashboardTheme();
  const { mutate: generateScript, isPending } = useScriptGeneration();
  const { mutate: generatePersonalizedSections, isPending: isGeneratingSections } = useGeneratePersonalizedScriptSections();
  const { mutate: updateScriptMeta, isPending: isUpdating } =
    useUpdateScriptMeta();
  const { id, tenantid, role } = useAuthStore();
  const effectiveTenantId = localStorage.getItem("tenantId") ?? tenantid;
  const [showAddScriptUI, setShowAddScriptUI] = useState(false);
  const { data: userData, isLoading: isUsersLoading } = useUsers({
    currentPage: 1,
    pageSize: 50,
    search: "",
    role: "",
    currentAuthRole: role,
  });
  const { data: teamData, isLoading: isTeamsLoading } = useTeams(effectiveTenantId);

  const [selectedInsightIndex, setSelectedInsightIndex] = useState<number | null>(null);
  const [scriptAssignments, setScriptAssignments] = useState<any[]>([]);
  const [editScriptId, setEditScriptId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [form, setForm] = useState({
    title: "",
    scriptType: "",
    companyId: "",
    companyContactId: "",
    lessonOverview: "",
    budgetObjection: "",
    scriptedResponse: "",
    practicePrompt: "",
    assignedTo: [] as string[],
    isPracticeSection: {
      lessonOverview: false,
      budgetObjection: false,
      scriptedResponse: true,
      practicePrompt: true,
    },
  });

  const selectedRepId = form.assignedTo.length === 1 ? form.assignedTo[0] : "";
  const {
    data: repInsightsResponse,
    isLoading: isInsightsLoading,
    isFetching: isInsightsFetching,
    refetch: refetchInsights
  } = useGetRepInsights(selectedRepId);
  const repInsights = repInsightsResponse?.data;

  const { data: companiesData, isLoading: isCompaniesLoading } = useTargetCompanies();
  const { data: contactsData, isLoading: isContactsLoading } = useCompanyContacts(form.companyId);

  const {
    data: companyInsightsResponse,
    isLoading: isCompanyInsightsLoading,
    isFetching: isCompanyInsightsFetching,
    refetch: refetchCompanyInsights
  } = useGetCompanyInsights(form.companyId);
  const companyInsights = companyInsightsResponse?.data;

  const [selectedCompanyInsightIndex, setSelectedCompanyInsightIndex] = useState<number | null>(null);

  const companyOptions = companiesData?.data?.items?.map((c: any) => ({
    label: c.companyName,
    value: c.id
  })) || [];

  const contactOptions = contactsData?.data?.items
    ?.map((c: any) => ({
      label: `${c.firstName} ${c.lastName} (${c.jobTitle || 'No Title'})`,
      value: c.id
    })) || [];

  React.useEffect(() => {
    if (Array.isArray(repInsights) && repInsights.length > 0 && selectedInsightIndex === null) {
      setSelectedInsightIndex(0);
      setForm((prev) => ({ ...prev, scriptType: repInsights[0].recommendedScriptType }));
    }
  }, [repInsights]);

  React.useEffect(() => {
    if (Array.isArray(companyInsights) && companyInsights.length > 0 && selectedCompanyInsightIndex === null) {
      setSelectedCompanyInsightIndex(0);
      setForm((prev) => ({ ...prev, scriptType: companyInsights[0].recommendedScriptType }));
    }
  }, [companyInsights]);

  const handleSelectInsight = (index: number, scriptType: string, isCompanyInsight = false) => {
    if (isCompanyInsight) {
        setSelectedCompanyInsightIndex(index);
        setSelectedInsightIndex(null);
    } else {
        setSelectedInsightIndex(index);
        setSelectedCompanyInsightIndex(null);
    }
    setForm((prev) => ({ ...prev, scriptType }));
  };

  const [formErrors, setFormErrors] = useState({
    title: "",
    scriptType: "",
    companyId: "",
    companyContactId: "",
    lessonOverview: "",
    budgetObjection: "",
    scriptedResponse: "",
    assignedTo: "",
    practicePrompt: "",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"error" | "success">(
    "error",
  );
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);

  const scriptTypeOptions = [
    { label: "Call", value: "call" },
    { label: "Demo", value: "demo" },
    { label: "Negotiation", value: "negotiation" },
    { label: "Objection", value: "objection" },
  ];

  const teamOptions =
    teamData?.data?.data?.map((team: { id: string; name: string }) => ({
      label: team.name,
      value: team.id,
    })) || [];

  const repOptions =
    userData?.data?.items?.map((user: User) => ({
      label: user.name || user.email,
      value: user.id,
    })) || [];

  const handleChange = (name: string, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePracticeSectionToggle = (field: string, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      isPracticeSection: {
        ...prev.isPracticeSection,
        [field]: checked,
      },
    }));
  };

  const handleRepsChange = (newVals: string[]) => {
    setForm((prev) => ({ ...prev, assignedTo: newVals }));
    setFormErrors((prev) => ({ ...prev, assignedTo: "" }));
    setSelectedInsightIndex(null);
  };

  const handleGenerateSections = () => {
    let companyInsightText: string | undefined = undefined;
    if (selectedCompanyInsightIndex !== null && companyInsights && companyInsights[selectedCompanyInsightIndex]) {
        companyInsightText = companyInsights[selectedCompanyInsightIndex].insight;
    }

    if (!companyInsightText && form.assignedTo.length !== 1) {
      setSnackbarMessage("Please select exactly one rep to generate a personalized script, or select a Company Insight.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (!form.scriptType) {
      setSnackbarMessage("Please select a Script Type first.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    generatePersonalizedSections(
      { 
        userId: form.assignedTo.length === 1 ? form.assignedTo[0] : undefined, 
        scriptType: form.scriptType, 
        companyId: form.companyId || undefined, 
        companyContactId: form.companyContactId || undefined,
        companyInsight: companyInsightText
      },
      {
        onSuccess: (data: any) => {
          setForm((prev) => ({
            ...prev,
            lessonOverview: data.data?.lessonOverview || "",
            budgetObjection: data.data?.budgetObjection || "",
            scriptedResponse: data.data?.scriptedResponse || "",
            practicePrompt: data.data?.practicePrompt || "",
          }));
          setSnackbarMessage("Personalized script sections generated successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        },
        onError: (err: any) => {
          let message = "Failed to generate sections";
          if (err instanceof Error) message = err.message;
          setSnackbarMessage(`Error: ${message}`);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      }
    );
  };

  const validateFields = () => {
    const isAdmin = role === "ADMIN" || role === "SUPER_ADMIN";
    const errors = {
      title: form.title.trim() === "" ? "Title is required" : "",
      scriptType:
        form.scriptType.trim() === "" ? "Script type is required" : "",
      lessonOverview:
        form.lessonOverview.trim() === "" ? "Lesson overview is required" : "",
      budgetObjection:
        form.budgetObjection.trim() === ""
          ? "Budget objection is required"
          : "",
      scriptedResponse:
        form.scriptedResponse.trim() === ""
          ? "Scripted response is required"
          : "",
      practicePrompt:
        form.practicePrompt.trim() === "" ? "Practice prompt is required" : "",
      companyId:
        !isAdmin && form.companyId.trim() === "" ? "Target company is required" : "",
      companyContactId: "",
      assignedTo: "",
    };

    setFormErrors(errors);
    return !Object.values(errors).some((err) => err !== "");
  };

  const resetForm = () => {
    setForm({
      title: "",
      scriptType: "",
      companyId: "",
      companyContactId: "",
      lessonOverview: "",
      budgetObjection: "",
      scriptedResponse: "",
      practicePrompt: "",
      assignedTo: [],
      isPracticeSection: {
        lessonOverview: false,
        budgetObjection: false,
        scriptedResponse: true,
        practicePrompt: true,
      },
    });
    setSelectedTeamIds([]);
    setFormErrors({
      title: "",
      scriptType: "",
      companyId: "",
      companyContactId: "",
      lessonOverview: "",
      budgetObjection: "",
      scriptedResponse: "",
      assignedTo: "",
      practicePrompt: "",
    });
  };

  type SectionSource = { sections: SectionDto[] };

  const getSectionContent = (
    script: SectionSource,
    type: SectionDto["type"],
  ): string => {
    return script.sections.find((s) => s.type === type)?.content || "";
  };

  const getIsPractice = (
    script: SectionSource,
    type: SectionDto["type"],
  ): boolean => {
    return (
      script.sections.find((s) => s.type === type)?.isPracticeSection || false
    );
  };

  const handleSave = () => {
    if (!validateFields()) return;

    const payload = {
      title: form.title.trim(),
      scripttype: form.scriptType.trim().toLowerCase(),
      assignToUserIds: form.assignedTo,
      companyId: form.companyId || undefined,
      companyContactId: form.companyContactId || undefined,
      sections: [
        {
          type: "LESSON_OVERVIEW",
          content: form.lessonOverview.trim(),
          isPracticeSection: form.isPracticeSection.lessonOverview,
        },
        {
          type: "UNDERSTAND_OBJECTION",
          content: form.budgetObjection.trim(),
          isPracticeSection: form.isPracticeSection.budgetObjection,
        },
        {
          type: "SCRIPTED_RESPONSE",
          content: form.scriptedResponse.trim(),
          isPracticeSection: form.isPracticeSection.scriptedResponse,
        },
        {
          type: "PRACTICE_PROMPT",
          content: form.practicePrompt.trim(),
          isPracticeSection: form.isPracticeSection.practicePrompt,
        },
      ],
      assignToTeamIds: selectedTeamIds.filter(Boolean),
      tenantId: effectiveTenantId,
      createdById: id,
    };

    const onSuccessHandler = () => {
      resetForm();
      setSnackbarMessage(
        isEditMode ? "Script updated!" : "Script created successfully!",
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        setShowAddScriptUI(false);
        setIsEditMode(false);
        setEditScriptId(null);
      }, 1500);
    };

    const onErrorHandler = (err: unknown) => {
      let message = "Something went wrong";
      if (err instanceof Error) message = err.message;
      setSnackbarMessage(`Error: ${message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    };

    if (isEditMode && editScriptId) {
      updateScriptMeta(
        { scriptMetaId: editScriptId, payload },
        {
          onSuccess: onSuccessHandler,
          onError: onErrorHandler,
        },
      );
    } else {
      generateScript(payload, {
        onSuccess: onSuccessHandler,
        onError: onErrorHandler,
      });
    }
  };

  return (
    <>
      {!showAddScriptUI && (
        <ScriptListView
          setShowAddScriptUI={setShowAddScriptUI}
          onEdit={(scriptData: ScriptDetails, value: boolean) => {
            setShowAddScriptUI(value);
            setIsEditMode(value);
            setEditScriptId(scriptData.scriptMeta.id);
            setScriptAssignments(scriptData.scriptMeta.ScriptGenAssignment || []);

            const assignedUserIds: string[] =
              scriptData.scriptMeta.ScriptGenAssignment?.map(
                (assignment) => assignment.user?.id,
              ).filter((id): id is string => Boolean(id)) ?? [];

            const assignedTeamIds: string[] =
              scriptData.scriptMeta.ScriptGenAssignment?.map(
                (assignment) => assignment.teamid,
              ).filter((teamId): teamId is string => Boolean(teamId)) ?? [];

            setForm({
              title: scriptData.scriptMeta.title,
              scriptType: scriptData.scriptMeta.scripttype,
              companyId: scriptData.scriptMeta.companyId || "",
              companyContactId: scriptData.scriptMeta.companyContactId || "",
              lessonOverview: getSectionContent(scriptData, "LESSON_OVERVIEW"),
              budgetObjection: getSectionContent(
                scriptData,
                "UNDERSTAND_OBJECTION",
              ),
              scriptedResponse: getSectionContent(
                scriptData,
                "SCRIPTED_RESPONSE",
              ),
              practicePrompt: getSectionContent(scriptData, "PRACTICE_PROMPT"),
              assignedTo: assignedUserIds,
              isPracticeSection: {
                lessonOverview: getIsPractice(scriptData, "LESSON_OVERVIEW"),
                budgetObjection: getIsPractice(
                  scriptData,
                  "UNDERSTAND_OBJECTION",
                ),
                scriptedResponse: getIsPractice(
                  scriptData,
                  "SCRIPTED_RESPONSE",
                ),
                practicePrompt: getIsPractice(scriptData, "PRACTICE_PROMPT"),
              },
            });

            setSelectedTeamIds(assignedTeamIds);
          }}
        />
      )}

      {showAddScriptUI && (
        <Box
          className={isDark ? "" : "light-theme-dashboard"}
          minHeight="100vh"
          sx={{ backgroundColor: "var(--color-bg)", color: "var(--color-text)", py: 4, pt: 8 }}
        >
          <Box
            sx={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              p: 4,
              borderRadius: "16px",
              mx: "auto",
              maxWidth: "1000px",
              m: 2,
            }}
          >
            <Box mx="auto">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={4}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", fontSize: 20 }}>
                  {isEditMode ? "Edit Script" : "Create New Script"}
                </Typography>
                <Button
                  onClick={() => {
                    setShowAddScriptUI(false);
                    resetForm();
                  }}
                  sx={{
                    backgroundColor: "var(--color-surface)",
                    color: "var(--color-text)",
                    fontWeight: "bold",
                    borderRadius: 999,
                    px: 3,
                    py: 1,
                    textTransform: "none",
                    border: "1px solid var(--color-border)",
                    "&:hover": { backgroundColor: "var(--color-divider)" },
                  }}
                >
                  Go Back
                </Button>
              </Box>


              {/* Step 1: Assignment */}
              <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={3} mb={4}>
                <Box flex={1}>
                  <MultiSelectDropdown
                    label="Assign to Rep (Optional)"
                    value={form.assignedTo}
                    options={isUsersLoading ? [{ label: "Loading...", value: "" }] : repOptions}
                    onChange={handleRepsChange}
                    multiSelect={false} // Assuming we want single rep for AI insights
                    disabled={isUsersLoading}
                    error={formErrors.assignedTo}
                  />
                  {form.assignedTo.length > 0 && (
                    <Box mt={1}>
                      {form.assignedTo.map((repId) => {
                        const assignment = scriptAssignments.find(a => a.user?.id === repId);
                        return (
                          <Typography key={repId} variant="body2" sx={{ color: "var(--color-textMuted)", fontSize: 13 }}>
                            Reassigned: {assignment?.reassignCount || 0} times | Attempted: {assignment?.attemptCount || 0} times
                          </Typography>
                        );
                      })}
                    </Box>
                  )}
                </Box>
                <Box flex={1}>
                  <MultiSelectDropdown
                    label="Select Teams (Optional)"
                    value={selectedTeamIds}
                    options={isTeamsLoading ? [{ label: "Loading...", value: "" }] : teamOptions}
                    onChange={setSelectedTeamIds}
                    multiSelect
                    disabled={isTeamsLoading}
                  />
                </Box>
              </Box>

                            {/* Step 2: Rep Insights */}
              {form.assignedTo.length > 0 && (
                <RepInsightsList
                  insights={Array.isArray(repInsights) ? repInsights : []}
                  isLoading={isInsightsLoading}
                  isFetching={isInsightsFetching}
                  onRefresh={refetchInsights}
                  selectedInsightIndex={selectedInsightIndex}
                  onSelectInsight={handleSelectInsight}
                />
              )}

              {form.companyId && (
                <CompanyInsightsList
                  insights={Array.isArray(companyInsights) ? companyInsights : []}
                  isLoading={isCompanyInsightsLoading}
                  isFetching={isCompanyInsightsFetching}
                  onRefresh={refetchCompanyInsights}
                  selectedInsightIndex={selectedCompanyInsightIndex}
                  onSelectInsight={handleSelectInsight}
                />
              )}

              {/* Step 3: Script Configuration */}
              <Box mb={4} p={3} sx={{ background: "var(--color-bg)", borderRadius: "16px", border: "1px solid var(--color-border)" }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, fontSize: 18 }}>
                  Script Details
                </Typography>

                <Box display="flex" gap={3} mb={3}>
                  <Box flex={1}>
                    <CustomInput
                      label="Title"
                      placeholder="Enter script title"
                      name="title"
                      value={form.title}
                      onChange={(e) => handleChange("title", e.target.value)}
                      error={formErrors.title}
                      required
                    />
                  </Box>
                  <Box flex={1}>
                    <MultiSelectDropdown
                      label="Script Type"
                      placeholder="Select script type"
                      value={[form.scriptType]}
                      options={scriptTypeOptions}
                      onChange={(newVal) => handleChange("scriptType", newVal[0])}
                      required
                      multiSelect={false}
                      error={formErrors.scriptType}
                    />
                  </Box>
                </Box>

                <Box display="flex" gap={3} mb={3}>
                  <Box flex={1}>
                    <MultiSelectDropdown
                      label={`Target Company${role === "ADMIN" || role === "SUPER_ADMIN" ? " (Optional)" : ""}`}
                      placeholder="Select target company"
                      value={form.companyId ? [form.companyId] : []}
                      options={companyOptions}
                      onChange={(newVal) => handleChange("companyId", newVal[0] || "")}
                      multiSelect={false}
                      disabled={isCompaniesLoading}
                      required={!(role === "ADMIN" || role === "SUPER_ADMIN")}
                      error={formErrors.companyId}
                    />
                  </Box>
                  <Box flex={1}>
                    <MultiSelectDropdown
                      label={`Buyer Persona${role === "ADMIN" || role === "SUPER_ADMIN" ? " (Optional)" : ""}`}
                      placeholder="Select buyer persona"
                      value={form.companyContactId ? [form.companyContactId] : []}
                      options={contactOptions}
                      onChange={(newVal) => handleChange("companyContactId", newVal[0] || "")}
                      multiSelect={false}
                      disabled={!form.companyId || isContactsLoading}
                      required={!(role === "ADMIN" || role === "SUPER_ADMIN")}
                      error={formErrors.companyContactId}
                    />
                  </Box>
                </Box>

                <CustomInput
                  label="Lesson Overview"
                  placeholder="Enter lesson overview"
                  name="lessonOverview"
                  required
                  multiline
                  rows={4}
                  value={form.lessonOverview}
                  onChange={(e) => handleChange("lessonOverview", e.target.value)}
                  error={formErrors.lessonOverview}
                  className="mb-4"
                />

                <CustomInput
                  label="Understanding the Purpose"
                  placeholder="Enter purpose"
                  name="budgetObjection"
                  value={form.budgetObjection}
                  onChange={(e) => handleChange("budgetObjection", e.target.value)}
                  multiline
                  rows={4}
                  required
                  error={formErrors.budgetObjection}
                  className="mb-4"
                />

                <CustomInput
                  label="Scripted Response"
                  placeholder="Enter scripted response"
                  name="scriptedResponse"
                  required
                  multiline
                  rows={4}
                  value={form.scriptedResponse}
                  onChange={(e) => handleChange("scriptedResponse", e.target.value)}
                  error={formErrors.scriptedResponse}
                  isCheckbox
                  checkboxValue={form.isPracticeSection.scriptedResponse}
                  onCheckboxChange={(checked) => handlePracticeSectionToggle("scriptedResponse", checked)}
                  checkBoxLabel="Mark as Practice"
                  className="mb-4"
                />

                <CustomInput
                  label="Practice Prompt"
                  placeholder="Enter practice prompt"
                  name="practicePrompt"
                  required
                  multiline
                  rows={4}
                  value={form.practicePrompt}
                  onChange={(e) => handleChange("practicePrompt", e.target.value)}
                  error={formErrors.practicePrompt}
                  isCheckbox
                  checkboxValue={form.isPracticeSection.practicePrompt}
                  onCheckboxChange={(checked) => handlePracticeSectionToggle("practicePrompt", checked)}
                  checkBoxLabel="Mark as Practice"
                />
              </Box>

              {/* Actions */}
              <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" mt={4} pt={4} sx={{ borderTop: "1px solid var(--color-border)" }}>
                <Button
                  onClick={handleGenerateSections}
                  disabled={isGeneratingSections || (!repInsights && !companyInsights)}
                  sx={{
                    backgroundColor: isDark ? "rgba(108,99,255,0.12)" : "rgba(108,99,255,0.08)",
                    color: isDark ? "#A78BFA" : "#5B21B6",
                    border: isDark ? "1px solid rgba(108,99,255,0.25)" : "1px solid rgba(108,99,255,0.2)",
                    fontWeight: "bold",
                    borderRadius: "12px",
                    px: 3,
                    py: 1.5,
                    textTransform: "none",
                    "&:hover": { backgroundColor: "rgba(108,99,255,0.3)" },
                    "&:disabled": { borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.3)", backgroundColor: "transparent" }
                  }}
                >
                  {isGeneratingSections ? "Generating AI Script..." : "Auto-Generate with AI"}
                </Button>

                <Button
                  onClick={handleSave}
                  disabled={isPending}
                  sx={{
                    width: "fit-content",
                    backgroundColor: "#C1FF72",
                    color: "#000",
                    fontWeight: "bold",
                    borderRadius: 999,
                    px: 4,
                    py: 1.5,
                    mt: { xs: 2, sm: 0 },
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#D7FF9B" },
                  }}
                >
                  {isPending || isUpdating ? "Submitting..." : isEditMode ? "Update Script" : "Save & Assign Script"}
                </Button>
              </Box>
            </Box>
          </Box>


          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSnackbarOpen(false)}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      )}
    </>
  );
};

export default Script;
